// Recorrências, dashboard, sino e perfil — ver docs/API.md do planbackgo.
import type { Activity, Priority } from './activity';
import type { Board } from './board';
import type { ExpiringItem } from './cac';
import type { BudgetSummary, CreditCardInvoice, ScheduleStatus } from './finance';

// ---------------------------------------------------------------- recorrências

export type Frequency = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface RecurrenceRule {
  id: number;
  board_id: number;
  board_name: string;
  board_column_id?: number;
  title: string;
  description?: string;
  priority: Priority;
  frequency: Frequency;
  interval_count: number;
  /** Máscara bit0=domingo .. bit6=sábado (só weekly). */
  days_of_week: number;
  /** 0 = mesmo dia do início. */
  day_of_month: number;
  start_date: string;
  end_date?: string;
  lead_days: number;
  last_generated_date?: string;
  next_occurrence?: string;
  is_active: boolean;
  created_at: string;
  created_by: string;
  updated_at?: string;
  last_updated_by?: string;
}

export interface RecurrenceFormData {
  board_id: number;
  board_column_id?: number | null;
  title: string;
  description?: string | null;
  priority: Priority;
  frequency: Frequency;
  interval_count: number;
  days_of_week: number;
  day_of_month: number;
  start_date: string;
  end_date?: string | null;
  lead_days: number;
  is_active?: boolean;
}

// ---------------------------------------------------------------- dashboard ("Hoje")

export interface DashboardSummary {
  today: string;
  due_today: number;
  overdue: number;
  no_due_date: number;
  critical: number;
  /** Abertas com prazo entre amanhã e daqui a 7 dias. */
  upcoming: number;
  completed_today: number;
  completed_last_7_days?: number;
  unread_notifications: number;
  due_today_list: Activity[];
  overdue_list: Activity[];
  no_due_date_list: Activity[];
  upcoming_list: Activity[];
  /** Projetos ativos (com período) do usuário. */
  boards: Board[];
  /** Vencimentos consolidados (até 10): documentos CAC (só com `features.cac`) e documentos com validade. */
  expiring_documents: ExpiringItem[];
  expiring_count: number;
  /** Fase 4: faturas de cartão não pagas vencendo nos próximos 30 dias ou vencidas (até 5, por vencimento). */
  upcoming_invoices: CreditCardInvoice[];
  /** Fase 5: orçamento do mês (as 5 orçadas de maior pct). Ausente quando não há orçamento. */
  budget?: BudgetSummary | null;
  /** Fase 6: resumo financeiro (saldos, mês corrente realizado, contas). Ausente sem conta ativa. */
  finance?: FinanceSummary | null;
  /** Fase 7: contas pagas por agendamento no banco. Ausente sem lançamento que exija agendamento (60 dias) nem vencido. */
  scheduling?: SchedulingSummary | null;
}

/** Despesa que precisa ser agendada no banco (ordem `transaction_date`). */
export interface ScheduleItem {
  transaction_id: number;
  account_id: number;
  account_name: string;
  transaction_date: string;
  description: string;
  amount_cents: number;
  category_name?: string | null;
  recurring_transaction_id?: number | null;
  schedule_status: ScheduleStatus;
  /** Negativo quando já venceu. */
  days_until_due: number;
}

/** `DashboardSummary.scheduling` (Fase 7). */
export interface SchedulingSummary {
  to_schedule: ScheduleItem[];
  missed: ScheduleItem[];
  scheduled_next_30_count: number;
  scheduled_next_30_cents: number;
  upcoming_next_30_count: number;
}

/** Conta ativa no resumo do painel (até 6, por nome). */
export interface FinanceAccountSummary {
  id: number;
  name: string;
  color?: string | null;
  balance_cents: number;
}

/**
 * `DashboardSummary.finance`: saldo de hoje das contas ativas, saldo previsto até o fim do mês
 * corrente e o mês realizado (receitas, despesas com sinal e resultado — mesma base do
 * `/transactions/summary`, sem transferências nem espelhos de fatura).
 */
export interface FinanceSummary {
  accounts_balance_cents: number;
  projected_balance_cents: number;
  month_income_cents: number;
  month_expense_cents: number;
  month_net_cents: number;
  /** Lançamentos do mês corrente sem categoria. */
  uncategorized_count: number;
  accounts: FinanceAccountSummary[];
}

// ---------------------------------------------------------------- sino

/** Tipos de alerta de todos os módulos (os de finanças/documentos chegam nas fases seguintes). */
export type NotificationType =
  | 'overdue'
  | 'due_today'
  | 'critical'
  | 'doc_expiring'
  | 'bill_due'
  | 'budget_exceeded'
  | 'schedule_pending';

export type NotificationRefType = 'activity' | 'document' | 'cac_document' | 'invoice' | 'category' | 'transaction';

export interface AppNotification {
  id: number;
  type: NotificationType;
  ref_type?: NotificationRefType;
  ref_id?: number;
  /** Preenchidos quando ref_type = activity. */
  activity_id?: number;
  activity_title?: string;
  message: string;
  notification_date: string;
  read_at?: string;
  created_at: string;
}

// ---------------------------------------------------------------- perfil

export type AppRole = 'admin' | 'user';

/** Provedor de identidade do cadastro (Fase 8). */
export type AppProvider = 'entra' | 'google';
export const PROVIDER_LABELS: Record<AppProvider, string> = { entra: 'Microsoft', google: 'Google' };

/** Plano do cadastro (Fase 8). `trial` tem `access_until`; `free`/`paid` podem ter ou não. */
export type AppPlan = 'free' | 'trial' | 'paid';
export const PLAN_LABELS: Record<AppPlan, string> = { free: 'Gratuito', trial: 'Teste', paid: 'Pago' };

/** Módulos liberados para o usuário (o admin sempre tem todos). */
export interface ProfileFeatures {
  /** Acervo CAC: liberação geral (Administração → Configurações) ou individual (Usuários). */
  cac: boolean;
}

export interface Profile {
  user_id: number;
  email: string;
  name: string;
  role: AppRole;
  is_admin: boolean;
  provider?: AppProvider;
  plan?: AppPlan;
  /** "AAAA-MM-DD"; ausente = sem data de término. */
  access_until?: string | null;
  /** `expired` quando `access_until` já passou: o backend responde 403 `access_expired` em tudo, exceto /auth/me|profile|logout. */
  access_status?: 'active' | 'expired';
  /** Dias até `access_until` (só quando há data). */
  days_left?: number | null;
  features?: ProfileFeatures;
}

export type AppUserAccessStatus = 'active' | 'expired' | 'blocked';

export interface AppUser {
  user_id: number;
  object_id?: string;
  provider: AppProvider;
  email: string;
  name: string;
  role: AppRole;
  is_active: boolean;
  plan: AppPlan;
  /** "AAAA-MM-DD"; ausente = sem data de término. */
  access_until?: string | null;
  /** Derivado: `blocked` (is_active = false), `expired` (access_until < hoje) ou `active`. */
  access_status: AppUserAccessStatus;
  /** Liberação individual do Acervo CAC (interruptor em Administração → Usuários). */
  cac_enabled: boolean;
  /** Resultado da regra: admin sempre; os demais pela liberação geral OU individual. */
  cac_effective: boolean;
  created_at?: string;
  last_login_at?: string | null;
  /** Última requisição (gravada em lote a cada 60 s). */
  last_seen_at?: string | null;
  sessions_30d?: number;
  requests_30d?: number;
}

/** PATCH /admin/users/:id — campos opcionais; só os enviados mudam (`access_until: null` = sem data). */
export interface AppUserPatch {
  is_active?: boolean;
  cac_enabled?: boolean;
  plan?: AppPlan;
  access_until?: string | null;
}

/** GET /admin/users/:id/sessions (Fase 8) — últimas 50 sessões, mais recente primeiro. */
export interface UserSession {
  session_id: number;
  provider: AppProvider;
  ip_address?: string | null;
  user_agent?: string | null;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
  revoked_at?: string | null;
  revoked_by?: string | null;
  request_count: number;
  /** A sessão de quem está consultando (o próprio admin). */
  is_current: boolean;
}

/** Módulos contados no uso (`domain.ModuleOf(path)`). */
export type UsageModule = 'agenda' | 'documentos' | 'acervo' | 'financas' | 'admin' | 'auth';
export const USAGE_MODULES: UsageModule[] = ['agenda', 'documentos', 'acervo', 'financas', 'admin', 'auth'];
export const USAGE_MODULE_LABELS: Record<UsageModule, string> = {
  agenda: 'Agenda',
  documentos: 'Documentos',
  acervo: 'Acervo CAC',
  financas: 'Finanças',
  admin: 'Administração',
  auth: 'Autenticação',
};

export interface UsageRow {
  date: string;
  module: UsageModule | string;
  requests: number;
}

/** GET /admin/users/:id/usage?days=N (Fase 8). */
export interface UsageReport {
  days: number;
  from: string;
  to: string;
  rows: UsageRow[];
  totals: Record<string, number>;
  total: number;
}

export type EmailStatus = 'pending' | 'sending' | 'sent' | 'failed';
export const EMAIL_STATUS_LABELS: Record<EmailStatus, string> = {
  pending: 'Pendente',
  sending: 'Enviando',
  sent: 'Enviado',
  failed: 'Falhou',
};
export const EMAIL_TEMPLATE_LABELS: Record<string, string> = {
  schedule_pending: 'Contas a agendar',
  welcome: 'Boas-vindas',
  signup_admin: 'Novo cadastro (admin)',
  access_extended: 'Acesso estendido',
};

/** GET /admin/emails (Fase 8) — caixa de saída, mais recente primeiro. */
export interface EmailOutboxItem {
  email_id: number;
  user_id: number;
  to_email: string;
  to_name?: string | null;
  subject: string;
  template_key: string;
  status: EmailStatus;
  attempts: number;
  next_attempt_at?: string | null;
  sent_at?: string | null;
  last_error?: string | null;
  created_at: string;
}

/** GET/PUT /admin/settings (tabela global AppSetting). */
export interface AppSettings {
  cac_enabled_for_all: boolean;
  /** Login com Google cria a conta na hora (Fase 8). Desligado → 403 `signup_disabled`. */
  signup_enabled: boolean;
  /** Dias de teste do cadastro novo (1–365). */
  trial_days: number;
  updated_at?: string;
  updated_by?: string;
}

/** PUT /admin/settings — ao menos um campo. */
export interface AppSettingsPatch {
  cac_enabled_for_all?: boolean;
  signup_enabled?: boolean;
  trial_days?: number;
}
